// Push notification setup — wraps Firebase Cloud Messaging for the app.
//
// Flow:
//   1. App calls ensurePushSetup(userId) on guard/owner login.
//   2. We check browser support, then permission state.
//   3. If permission is "default", we DO NOT prompt automatically — too
//      aggressive. The user grants it via a deliberate UI action ("Enable
//      reminders" toggle in Profile / Settings).
//   4. Once granted, we get an FCM token and store it on the user doc.
//   5. Foreground messages (app open in current tab) show as in-app toasts.
//
// Sending notifications happens server-side via a Cloud Function (not
// included here — see docs/PUSH_NOTIFICATIONS.md for deploy steps). The
// client only handles registration and reception.

import { getToken, onMessage } from "firebase/messaging";
import { doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions, getMessagingIfSupported } from "../firebase";

// Web Push needs a VAPID public key. Generated once in Firebase Console:
// Project Settings → Cloud Messaging → Web Push certificates → "Generate
// key pair". Copy the public key here. Safe to commit (it's a public key).
const VAPID_KEY = "BOy4RJv2ENcPLoEu6m73kDuQu1-F_iXENIY_ufrr4Wi11XHPmowm0qdQsh14cydoK2mZEGwJJYK060Fk45RltGM";

let foregroundUnsub = null;

/**
 * Returns the current notification permission state without prompting.
 * "granted" | "denied" | "default" | "unsupported"
 */
export async function getNotificationPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  const messaging = await getMessagingIfSupported();
  if (!messaging) return "unsupported";
  return Notification.permission;
}

/**
 * Prompt for permission and register the FCM token on the user doc.
 * Call from a button click — browsers block prompts that aren't user-initiated.
 */
export async function enablePushNotifications(userId, onForegroundMessage) {
  const messaging = await getMessagingIfSupported();
  if (!messaging) {
    return { ok: false, reason: "Push notifications aren't supported on this browser." };
  }
  if (Notification.permission === "denied") {
    return {
      ok: false,
      reason: "Notifications are blocked. Enable them in your browser settings, then try again.",
    };
  }
  if (Notification.permission === "default") {
    const result = await Notification.requestPermission();
    if (result !== "granted") {
      return { ok: false, reason: "Permission not granted." };
    }
  }
  // Register the service worker (the browser's persistent push handler).
  let registration;
  try {
    registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  } catch (err) {
    return { ok: false, reason: "Couldn't register service worker: " + (err?.message || err) };
  }
  // Get an FCM token tied to this device. Stored on the user doc so the
  // server-side sender (Cloud Function) can target this guard's phone.
  let token;
  try {
    token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
  } catch (err) {
    return { ok: false, reason: "Couldn't get push token: " + (err?.message || err) };
  }
  if (!token) {
    return { ok: false, reason: "No push token returned. Try again or check the VAPID key." };
  }
  // Add to user's tokens array (a guard can have multiple — phone + tablet).
  try {
    await updateDoc(doc(db, "users", userId), {
      pushTokens: arrayUnion(token),
      pushEnabledAt: new Date().toISOString(),
    });
  } catch (err) {
    return { ok: false, reason: "Couldn't save token: " + (err?.message || err) };
  }
  // Subscribe to foreground messages (app open in this tab). The service
  // worker handles background; this catches messages while the user is
  // looking at the app and shows them as a small toast.
  if (foregroundUnsub) foregroundUnsub();
  foregroundUnsub = onMessage(messaging, (payload) => {
    if (typeof onForegroundMessage === "function") onForegroundMessage(payload);
  });
  return { ok: true, token };
}

/**
 * Remove the current device's token from the user doc. Useful for "sign out
 * of notifications" without revoking browser permission.
 */
export async function disablePushNotifications(userId) {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return { ok: false, reason: "Not supported" };
  try {
    const registration = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (token) {
      await updateDoc(doc(db, "users", userId), {
        pushTokens: arrayRemove(token),
      });
    }
  } catch (err) {
    return { ok: false, reason: err?.message || String(err) };
  }
  if (foregroundUnsub) { foregroundUnsub(); foregroundUnsub = null; }
  return { ok: true };
}

/**
 * QA helper. Calls the sendTestPush callable, which fires a notification
 * to the current user's tokens with no other conditions. Use this to
 * confirm push delivery works without rebuilding the full day-before
 * scenario.
 */
export async function sendTestPush() {
  const fn = httpsCallable(functions, "sendTestPush");
  const result = await fn();
  return result.data; // { success, failure, pruned, errors }
}
